import { simd } from 'wasm-feature-detect';
import { wrap } from 'comlink';
import { detect } from 'detect-browser';
import { debugTypes } from './constants';
import { Base64, ImageType, LOGTYPE, WorkerFunctions } from './types';

const functions: WorkerFunctions = wrap(
  new Worker(new URL('../workers/wasm.worker.js', import.meta.url), { type: 'module' }),
);

export const {
  isLoad,
  loadAntispoof,
  isValidInternal,
  prividAgePredict,
  FHE_enroll,
  FHE_enrollOnefa,
  FHE_predictOnefa,
  voicePredict,
  isValidVoice,
  scanDocument,
  deleteUUID,
  isValidBarCode,
  prividFaceISO,
  prividFaceCompareLocal,
  antispoofCheck,
} = functions;

export function iOS(): boolean {
  return ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
    navigator.platform,
  );
}
export function isMobileFunc(): boolean {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}

export const getIsSIMD = async (): Promise<boolean> => {
  const isSimd = await simd();
  return isSimd;
};

export const getUrlParameter = function getUrlParameter(sParam: string, defaultValue: string) {
  const sPageURL = window.location.search.substring(1);
  const sURLVariables = sPageURL.split('&');
  let sParameterName;
  let i;

  for (i = 0; i < sURLVariables.length; i++) {
    sParameterName = sURLVariables[i].split('=');

    if (sParameterName[0] === sParam) {
      return typeof sParameterName[1] === undefined ? defaultValue : decodeURIComponent(sParameterName[1]);
    }
  }
  return defaultValue;
};

export const getDebugType = (): string => {
  let debugType = getUrlParameter('debug_type', '0');

  if (!debugTypes.includes(debugType)) {
    debugType = '0';
  }

  // if (debugType === '0' || debugType === '900') {
  //   window.console = {
  //     ...window.console,
  //     log: () => {},
  //     error: () => {},
  //     warn: () => {},
  //   };
  // }

  return debugType;
};

/**
 * This function does the printing in the console based on the type and will only print based on @param debugType
 * Please make sure to use this so that logs on the module would only print depending on the debug level
 * @param title title of the printing. sample console.log(tiltle, message)
 * @param message message of the printing. sample console.log(tiltle, message) if this is not needed just throw an empy string. sample: printLogs(title, '', debugType)
 * @param debugType this variable will determine the type of printing in the console
 * @param type this variable will determing the type of printing in the console, "LOG", "WARNING" or "ERROR" by default it is "LOG"
 */
export const printLogs = (title: string, message: any, debugType: string, type: LOGTYPE = LOGTYPE.LOG) => {
  const errorLogs = ['1', '2', '3', '901', '902', '903'];
  const warningLogs = ['2', '3', '902', '903'];
  const allLogs = ['3', '903'];
  if (type === LOGTYPE.ERROR && errorLogs.includes(debugType.toString())) {
    console.error(title, message);
  } else if (type === LOGTYPE.WARN && warningLogs.includes(debugType.toString())) {
    console.warn(title, message);
  } else if (type === LOGTYPE.LOG && allLogs.includes(debugType.toString())) {
    console.log(title, message);
  }
};

export const getPortraitBase64 = async (content: ImageData): Promise<Base64> => {
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  canvas.width = content.width;
  canvas.height = content.height;
  const context: CanvasRenderingContext2D = canvas.getContext('2d');
  context.putImageData(content, 0, 0);

  const blob = await new Promise<Blob>((resolve) => canvas.toBlob(resolve));
  const base64 = await blobToBase64(blob);
  return base64;
};

export const createImages = async (images: Array<ImageData>, title: ImageType, action: boolean): Promise<void> => {
  const promises = images.map(
    (content: ImageData) =>
      new Promise<{ url: string; base64: Base64 }>(async (resolve) => {
        const canvas: HTMLCanvasElement = document.createElement('canvas');
        canvas.width = content.width;
        canvas.height = content.height;
        const context: CanvasRenderingContext2D = canvas.getContext('2d');
        context.putImageData(content, 0, 0);

        const blob = await new Promise<Blob>((resolve) => canvas.toBlob(resolve, 'image/jpeg'));
        const base64 = await blobToBase64(blob);

        const url = URL.createObjectURL(blob);
        resolve({ url, base64 });
      }),
  );

  const data = await Promise.all(promises);
  const a = document.createElement('a');

  for (let i = 0; i < data.length; i++) {
    // if(!iOS()) {
    // eslint-disable-next-line no-loop-func
    await new Promise<void>((resolve) => {
      a.href = data[i].url;
      a.type = 'image/png';
      a.download = `${title}_${action ? 'enroll' : 'predict'}` + '.png';
      document.body.appendChild(a);

      a.click();
      setTimeout(() => {
        resolve();
      }, 1000);
    });
    // }

    // postImages(data[i].base64);
  }
};

function blobToBase64(blob: Blob): Promise<Base64> {
  return new Promise((resolve) => {
    const reader: FileReader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

// export function downloadBase64File(base64Data, fileName) {
//   const downloadLink = document.createElement("a");
//   downloadLink.href = base64Data;
//   downloadLink.download = fileName;
//   downloadLink.click();
// }

export const wait = async (timeout: number) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve('true');
    }, timeout);
  });

export const arrayToTextDownload = (data: Array<string>, filename: string) => {
  console.log('--------', data, '--------', filename);

  // const text = data.join(', ');
  // const blob = new Blob([text], { type: 'text/plain' });
  // const url = URL.createObjectURL(blob);
  // const a = document.createElement('a');
  // a.href = url;
  // a.download = filename;
  // document.body.appendChild(a);
  // a.click();
};

export function setDefaultCameraDeviceId(deviceId: string) {
  localStorage.setItem('defaultDeviceId', deviceId);
}

export function getDefaultCameraDeviceId() {
  return localStorage.getItem('defaultDeviceId');
}

// eslint-disable-next-line consistent-return
export const convertCroppedImage = async (croppedBarcode: any, cropImageWidth: number, cropImageHeight: number) => {
  try {
    const image = new ImageData(croppedBarcode, cropImageWidth, cropImageHeight);
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const context: CanvasRenderingContext2D = canvas.getContext('2d');
    context.putImageData(image, 0, 0);

    return canvas.toDataURL('image/png');
  } catch (e) {
    console.error(e);
  }
};

export const getStatusMessage = (result: number) => {
  const { os } = detect();
  if (os === 'Android OS' || os === 'iOS') {
    switch (result) {
      case -100:
        return 'Invalid Image';
      case -1:
        return 'No Face Detected';
      case 0:
        return 'Valid Biometric';
      case 1:
        return 'Image Spoof';
      case 2:
        return 'Video Spoof';
      case 3:
        return 'Too close';
      case 4:
        return 'Too far away';
      case 5:
        return 'Too far to right';
      case 6:
        return 'Too far to left';
      case 7:
        return 'Too far up';
      case 8:
        return 'Too far down';
      case 9:
        return 'Too blurry';
      case 10:
        return 'Remove Glasses';
      case 11:
        return 'Mask on';
      case 12:
        return 'Chin too far left';
      case 13:
        return 'Chin too far right';
      case 14:
        return 'Lift phone up level to face';
      case 15:
        return 'Lower phone level to face';
      case 16:
        return 'Too Dark. Please increase light.';
      case 17:
        return 'Too Bright. Please decreace light.';
      default:
        return '';
    }
  } else {
    switch (result) {
      case -100:
        return 'Invalid Image';
      case -1:
        return 'No Face Detected';
      case 0:
        return 'Valid Biometric';
      case 1:
        return 'Image Spoof';
      case 2:
        return 'Video Spoof';
      case 3:
        return 'Too close';
      case 4:
        return 'Too far away';
      case 5:
        return 'Too far to right';
      case 6:
        return 'Too far to left';
      case 7:
        return 'Too far up';
      case 8:
        return 'Too far down';
      case 9:
        return 'Too blurry';
      case 10:
        return 'Remove Glasses';
      case 11:
        return 'Mask on';
      case 12:
        return 'Chin too far left';
      case 13:
        return 'Chin too far right';
      case 14:
        return 'Tilt camera down level to face';
      case 15:
        return 'Tilt camera up level to face';
      case 16:
        return 'Too Dark. Please increase light.';
      case 17:
        return 'Too Bright. Please decreace light.';
      default:
        return '';
    }
  }
};

export const checkPackageSupport = async (): Promise<{
  support: boolean;
  message?: string;
}> => {
  const { name: browserName, os, version } = detect();

  const parsedVersion = parseInt(version.split('.')[0], 10);
  console.log(`USER AGENT:`, { browserName, os, version, parsedVersion });
  if (os === 'Mac OS') {
    if (browserName === 'safari' && parseInt(version.split('.')[0], 10) <= 10) {
      return { support: false, message: 'Safari version 10 and below is not supported.' };
    }
  }

  if (os === 'iOS') {
    if (browserName === 'safari' && parseInt(version.split('.')[0], 10) <= 10) {
      return { support: false, message: 'Safari version 10 and below is not supported.' };
    }
    if (browserName === 'opera' && parseInt(version.split('.')[0], 10) <= 72) {
      return { support: false, message: 'Opera mobile version 72 and below is not support WASM' };
    }
  }

  if (os === 'Android OS' || os === 'android') {
    if (browserName === 'chrome' && parseInt(version.split('.')[0], 10) <= 96) {
      return { support: false, message: 'Android Chrome version 96 and below is not supported.' };
    }
    if (browserName === 'samsung' && parseInt(version.split('.')[0], 10) <= 6.2) {
      return { support: false, message: 'Samsung browser version 6 and below is not supported.' };
    }
    if (browserName === 'firefox' && parseInt(version.split('.')[0], 10) <= 94) {
      return { support: false, message: 'Firefox mobile version 94 and below is not supported.' };
    }
    if (browserName === 'opera' && parseInt(version.split('.')[0], 10) <= 72) {
      return { support: false, message: 'Opera mobile version 72 and below is not support WASM' };
    }
    if (browserName === 'android' && parseInt(version.split('.')[0], 10) <= 96) {
      return { support: false, message: 'Android Browser version 96 and below is not supported.' };
    }
  }

  if (browserName === 'ie') {
    return { support: false, message: 'Internet Explorer is not supported.' };
  }
  if (browserName === 'edge' && parseInt(version.split('.')[0], 10) <= 78) {
    return { support: false, message: 'Edge version 78 and below is not supported.' };
  }

  if (browserName === 'chrome' && parseInt(version.split('.')[0], 10) <= 57) {
    return { support: false, message: 'Chrome version 57 and below is not support WASM' };
  }

  if (browserName === 'firefox' && parseInt(version.split('.')[0], 10) <= 52) {
    return { support: false, message: 'Firefox version 52 and below is not supported.' };
  }

  if (browserName === 'opera' && parseInt(version.split('.')[0], 10) <= 43) {
    return { support: false, message: 'Opera version 43 and below is not support WASM' };
  }

  return { support: true };
};

export const getFrontDocumentStatusMessage = (result: number) => {
  switch (result) {
    case 0:
      return 'Success';
    case 12:
      return '';
    case -1:
      return 'MOVE CLOSER';
    case 18:
      return 'SHOW FRONT OF DOCUMENT';
    case 3:
      return 'ALMOST DONE . . .';
    case 4:
      return 'MOVE JUST A LITTLE CLOSER';
    case 9:
      return 'TOO BLURRY';
    case -2:
      return 'SYSTEM ERROR. Please try again later.';
    case 5:
    case 6:
    case 7:
    case 8:
      return '';
    default:
      return '';
  }
};

export const getBackDocumentStatusMessage = (result: number) => {
  switch (result) {
    case 0:
      return 'Success';
    case 12:
      return '';
    case -1:
    case 10:
      return 'MOVE CLOSER TO BARCODE';
    case 3:
      return 'ALMOST DONE . . .';
    case 4:
      return 'MOVE JUST A LITTLE CLOSER';
    case 18:
      return 'PRESENT BARCODE IN BOX';
    case 9:
      return 'TOO BLURRY';
    case -2:
      return 'SYSTEM ERROR. Please try again later.';
    default:
      return '';
  }
};
